import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthProvider";
import { useApp } from "../hooks/useApp";
import { useWallet } from "../hooks/useWallet";
import { usePremiumStatus } from "../hooks/usePremiumStatus";
import LanguageSwitcher from "./LanguageSwitcher";
import TokenIcon from "./icons/TokenIcon";

const TOKEN_TOOLTIP_KEY = "kf_token_tooltip_seen";

type TopBeltProps = {
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
};

export default function TopBelt({ onMenuToggle, isSidebarOpen }: TopBeltProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { state } = useApp();
  const { wallet, loading: walletLoading } = useWallet();
  const { isPremium } = usePremiumStatus();
  const [showTokenTooltip, setShowTokenTooltip] = useState(false);
  const tooltipDismissed = useRef(false);

  const isLoggedIn = !!user;
  const isOnboarding = location.pathname.startsWith("/onboarding");
  const isPaywall = location.pathname.startsWith("/paywall");
  const hasUnread = state.chats.some((chat) => chat.hasUnread);

  // Scripted-chat MVP (see apps/competition/mechat/scripted-chat-spec.md):
  // don't let the tokens coachmark interrupt a scripted first-chat scene.
  // TopBelt is rendered as a sibling of <Outlet/> (see AppRoot.tsx), so it
  // doesn't get the router's `:id` param — parse it from the pathname
  // instead, same approach as the isOnboarding/isPaywall checks above.
  // `state.chats` already holds the full chat docs (incl. scriptState) from
  // the existing chats-collection listener, so this needs no new
  // subscription.
  //
  // Simplification vs. the spec ("suppress until the first locked media
  // message has appeared"): detecting "a locked scripted media message is
  // visible" would need a dedicated messages-subcollection listener here
  // (TopBelt has no access to the open chat's messages otherwise — see the
  // scripted-chat integration report). Instead this suppresses for the
  // whole duration of an active (incomplete) scripted chat and lets the
  // tooltip fire on the next natural trigger afterwards — in practice, once
  // the script completes (composer returns to normal) or the user leaves
  // the chat, both of which come after the user has already seen the
  // locked-media beats mid-script.
  const activeChatId = /^\/chats\/([^/]+)/.exec(location.pathname)?.[1] ?? null;
  const activeChat = activeChatId ? state.chats.find((chat) => chat.id === activeChatId) : null;
  const isActiveScriptedChat = Boolean(
    activeChat?.scriptState?.scriptId && !activeChat.scriptState.completedAt
  );

  // Show one-time token tooltip to first-time users after wallet loads,
  // but never mid-onboarding, on a paywall/checkout page (the user is
  // already there to buy tokens, so the upsell coachmark is redundant), or
  // during an active scripted first chat (see above).
  useEffect(() => {
    if (
      !isLoggedIn ||
      isOnboarding ||
      isPaywall ||
      isActiveScriptedChat ||
      walletLoading ||
      tooltipDismissed.current
    )
      return;
    const seen = window.localStorage.getItem(TOKEN_TOOLTIP_KEY);
    if (!seen) {
      const timer = setTimeout(() => setShowTokenTooltip(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, isOnboarding, isPaywall, isActiveScriptedChat, walletLoading]);

  const dismissTokenTooltip = () => {
    tooltipDismissed.current = true;
    setShowTokenTooltip(false);
    window.localStorage.setItem(TOKEN_TOOLTIP_KEY, "1");
  };

  const handleTokenPlusClick = () => {
    if (isPremium) {
      navigate("/paywall/tokens");
    } else {
      navigate("/paywall/subscribe");
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex items-center px-3 py-2 bg-[#0a0a0a] border-b border-[#1a1a1a]">
      {/* Left side - Hamburger + Logo */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onMenuToggle}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-white hover:bg-[#1a1a1a] transition-colors"
          aria-label={t("topBelt.toggleMenu")}
        >
          {isSidebarOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Logo */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-sm font-bold transition-opacity hover:opacity-80"
        >
          <span className="bg-gradient-to-r from-brand-start to-brand-end bg-clip-text text-transparent">
            Kinky Fantasy
          </span>
        </button>
      </div>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-2">
        {!isLoggedIn ? (
          <>
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => navigate("/auth/signin")}
              className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-brand-start to-brand-end text-white rounded-lg transition-opacity hover:opacity-90"
            >
              {t("topBelt.signIn")}
            </button>
          </>
        ) : isOnboarding ? null : (
          <>
            {/* Token pill */}
            <div
              className="relative flex items-center gap-1 bg-[#1a1a1a] rounded-full px-2 py-1"
              aria-label={t("topBelt.tokens")}
            >
              {showTokenTooltip && !isPaywall && (
                <div className="absolute top-10 right-0 z-50 w-56 rounded-xl bg-[#1a1a1a] border border-gold/40 p-3 shadow-xl">
                  <p className="text-xs text-white font-semibold mb-1">
                    {t("topBelt.tokenTooltipTitle", { defaultValue: "Tokens" })}
                  </p>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {t("topBelt.tokenTooltipBody", {
                      defaultValue: "Tokens are spent on messages, photos and videos. Tap + to get some.",
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={dismissTokenTooltip}
                    className="mt-2 w-full text-xs font-semibold text-white bg-gradient-to-r from-brand-start to-brand-end rounded-lg py-1.5 transition-opacity hover:opacity-90"
                  >
                    {t("topBelt.tokenTooltipGotIt", { defaultValue: "Got it" })}
                  </button>
                  {/* Arrow */}
                  <div className="absolute -top-1.5 right-6 w-3 h-3 bg-[#1a1a1a] border-l border-t border-gold/40 rotate-45" />
                </div>
              )}
              <TokenIcon className="text-gold" size={24} />
              <span className="text-xs text-white font-semibold">
                {walletLoading ? "..." : (wallet?.currentTokens ?? 0)}
              </span>
              <button
                type="button"
                onClick={handleTokenPlusClick}
                className="ml-1 w-5 h-5 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end text-white rounded-full text-xs font-bold transition-opacity hover:opacity-90"
                aria-label={t("topBelt.buyTokens")}
              >
                +
              </button>
            </div>

            {/* Notifications */}
            <div className="relative">
              <button
                type="button"
                onClick={() => navigate("/chats")}
                className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-full text-white hover:bg-[#262626] transition-colors"
                aria-label={t("topBelt.notifications")}
              >
                <span className="relative">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 22a2 2 0 002-2h-4a2 2 0 002 2zm6-6V11a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2z" />
                  </svg>
                  {hasUnread && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-[#0a0a0a]" />
                  )}
                </span>
              </button>
            </div>
          </>
        )}

      </div>
    </header>
  );
}
